import { useState } from "react";

export interface QuarterPreset {
  id: string;
  label: string;
  from: string;
  to: string;
  /** True while the quarter is still in progress (shows points "so far") */
  isCurrent: boolean;
}

// Points quarters run Sep-Nov (Q4), Dec-Feb (Q1), Mar-May (Q2), Jun-Aug (Q3).
// The first tracked quarter is Q4 2025 (Sep 2025). New quarters are added
// automatically as soon as they start, so the selector never goes stale.
const FIRST_QUARTER_START = Date.UTC(2025, 8, 1); // 2025-09-01

const MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function quarterNumber(startMonth: number): { q: number; yearOffset: number } {
  switch (startMonth) {
    case 8: // Sep
      return { q: 4, yearOffset: 0 };
    case 11: // Dec
      return { q: 1, yearOffset: 1 };
    case 2: // Mar
      return { q: 2, yearOffset: 0 };
    case 5: // Jun
      return { q: 3, yearOffset: 0 };
    default:
      throw new Error(`Unexpected quarter start month: ${startMonth}`);
  }
}

export function buildQuarterPresets(now: Date = new Date()): QuarterPreset[] {
  const presets: QuarterPreset[] = [];
  let start = new Date(FIRST_QUARTER_START);

  while (start.getTime() <= now.getTime()) {
    const end = new Date(
      Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 3, 1)
    );
    const { q, yearOffset } = quarterNumber(start.getUTCMonth());
    const year = start.getUTCFullYear() + yearOffset;
    const firstMonth = MONTH_SHORT[start.getUTCMonth()]!;
    const lastMonth = MONTH_SHORT[(start.getUTCMonth() + 2) % 12]!;

    presets.push({
      id: `${year}-Q${q}`,
      label: `Q${q} ${year} (${firstMonth}-${lastMonth})`,
      from: start.toISOString(),
      to: end.toISOString(),
      isCurrent: now.getTime() < end.getTime(),
    });

    start = end;
  }

  // Newest first
  return presets.reverse();
}

const QUARTER_PRESETS: QuarterPreset[] = buildQuarterPresets();

export function getQuarterPreset(id: string): QuarterPreset | undefined {
  return QUARTER_PRESETS.find((p) => p.id === id);
}

interface QuarterSelectorProps {
  /**
   * Called whenever the selected period changes.
   * `id` is the quarter preset id (e.g. "2026-Q3") or "custom".
   */
  onPeriodChange: (from: string, to: string, label: string, id: string) => void;
  initialQuarter?: string;
}

export function QuarterSelector({
  onPeriodChange,
  initialQuarter,
}: QuarterSelectorProps) {
  const [selected, setSelected] = useState(
    initialQuarter ?? QUARTER_PRESETS[0]!.id
  );
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const handlePresetClick = (preset: QuarterPreset) => {
    setSelected(preset.id);
    onPeriodChange(preset.from, preset.to, preset.label, preset.id);
  };

  const handleCustomClick = () => {
    setSelected("custom");
    if (customFrom && customTo) {
      onPeriodChange(
        new Date(customFrom).toISOString(),
        new Date(customTo).toISOString(),
        "Custom Range",
        "custom"
      );
    }
  };

  const handleCustomDateChange = (from: string, to: string) => {
    setCustomFrom(from);
    setCustomTo(to);
    if (from && to && selected === "custom") {
      onPeriodChange(
        new Date(from).toISOString(),
        new Date(to).toISOString(),
        "Custom Range",
        "custom"
      );
    }
  };

  return (
    <div className="space-y-3">
      <div className="seg flex-wrap">
        {QUARTER_PRESETS.map((preset) => (
          <button
            key={preset.id}
            onClick={() => handlePresetClick(preset)}
            title={
              preset.isCurrent
                ? "Quarter in progress - shows points gained so far"
                : undefined
            }
            className={`seg-btn inline-flex items-center gap-2 ${
              selected === preset.id ? "seg-btn-on" : ""
            }`}
          >
            {preset.isCurrent && <span className="live-dot" />}
            {preset.label}
            {preset.isCurrent && (
              <span className="text-xs text-gray-400 font-normal">so far</span>
            )}
          </button>
        ))}
        <button
          onClick={handleCustomClick}
          className={`seg-btn ${selected === "custom" ? "seg-btn-on" : ""}`}
        >
          Custom range
        </button>
      </div>

      {selected === "custom" && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-400">From</label>
            <input
              type="date"
              value={customFrom}
              onChange={(e) =>
                handleCustomDateChange(e.target.value, customTo)
              }
              className="select"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-400">To</label>
            <input
              type="date"
              value={customTo}
              onChange={(e) =>
                handleCustomDateChange(customFrom, e.target.value)
              }
              className="select"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export { QUARTER_PRESETS };
